import { requestIdleCallbackSafe } from 'lib';
import { pubsub } from 'lib/pubsub';
import { text as xhrText } from 'lib/xhr';

// Thanks Steven Benner! - adapted from https://github.com/stevenbenner/jquery-powertip

const inCrosstable = (el: HTMLElement) => document.querySelector('.crosstable')?.contains(el);

const makeRender = (id: string) => async (el: HTMLAnchorElement) => {
  const url = (el.dataset.href || el.href).replace(/\?.+$/, '');
  return xhrText(url + '/mini').then(html => {
    const el = document.getElementById(id) as HTMLElement;
    el.innerHTML = html;
    pubsub.emit('content-loaded', el);
  });
};

const userPowertip = (el: HTMLElement, pos?: PowerTip.Placement) =>
  $(el)
    .removeClass('ulpt')
    .powerTip({
      render: makeRender('powerTip'),
      placement:
        pos || (el.getAttribute('data-pt-pos') as PowerTip.Placement) || (inCrosstable(el) ? 'n' : 's'),
    });

const gamePowertip = (el: HTMLElement) =>
  $(el)
    .removeClass('glpt')
    .powerTip({
      render: makeRender('miniGame'),
      placement: inCrosstable(el) ? 'n' : 'w',
      defaultSize: [264, 264],
      popupId: 'miniGame',
    });

function powerTipWith(el: HTMLElement, ev: Event, f: (el: HTMLElement) => void) {
  if ('ontouchstart' in window && !el.classList.contains('mobile-powertip')) return;
  f(el);
  $.powerTip.show(el, ev);
}

function onIdleForAll(par: HTMLElement, sel: string, f: (el: HTMLElement) => void) {
  requestIdleCallbackSafe(
    () => Array.prototype.forEach.call(par.querySelectorAll(sel), (el: HTMLElement) => f(el)), // do not codegolf to `f`
    800,
  );
}

function $as<T>(cashOrHtml: Cash | string): T {
  return (typeof cashOrHtml === 'string' ? $(cashOrHtml) : cashOrHtml)[0] as T;
}

const powertip: LichessPowertip = {
  watchMouse() {
    document.body.addEventListener('mouseover', e => {
      const t = e.target as HTMLElement;
      if (t.classList.contains('ulpt')) powerTipWith(t, e, userPowertip);
      else if (t.classList.contains('glpt')) powerTipWith(t, e, gamePowertip);
    });
  },
  manualGameIn(parent: HTMLElement) {
    onIdleForAll(parent, '.glpt', gamePowertip);
  },
  manualGame: gamePowertip,
  manualUser: userPowertip,
  manualUserIn(parent: HTMLElement) {
    onIdleForAll(parent, '.ulpt', userPowertip);
  },
};

export default powertip;

interface WithTooltip extends HTMLElement {
  displayController: DisplayController;
  hasActiveHover: boolean;
  forcedOpen: boolean;
}

type ScopedData = Record<string, any>;

const session: { scoped: ScopedData } & Record<string, any> = {
  // for each popupId
  scoped: {
    // isTipOpen: false,
    // isClosing: false,
    // tipOpenImminent: false,
    // activeHover: null,
    // desyncTimeout: null,
    // delayInProgress: false,
  },
  currentX: 0,
  currentY: 0,
  previousX: 0,
  previousY: 0,
  mouseTrackingActive: false,
  delayInProgress: false,
  windowWidth: 0,
  windowHeight: 0,
  scrollTop: 0,
  scrollLeft: 0,
};

const Collision = {
  none: 0,
  top: 1,
  bottom: 2,
  left: 4,
  right: 8,
};

$.fn.powerTip = function (opts) {
  // don't do any work if there were no matched elements
  if (!this.length) {
    return this;
  }

  // extend options and instantiate TooltipController
  const options = Object.assign({}, defaults, opts) as Options;
  const tipController = new TooltipController(options);

  // hook mouse and viewport dimension tracking, causes layout reflow
  requestIdleCallbackSafe(() => initTracking());

  // setup the elements
  this.each((_, el: WithTooltip) => {
    const $this = $(el);

    // handle repeated powerTip calls on the same element by destroying the
    // original instance hooked to it and replacing it with this call
    if ('displayController' in el) {
      $.powerTip.destroy(el);
    }

    // create hover controllers for each element
    el.displayController = new DisplayController($this, options, tipController);
  });

  // attach events to matched elements if the manual options is not enabled
  this.on({
    // mouse events
    mouseenter(event) {
      $.powerTip.show(this, event);
    },
    mouseleave() {
      $.powerTip.hide(this);
    },
  });

  return this;
};

type Options = PowerTip.Options & Required<Pick<PowerTip.Options, 'defaultSize'>>;

const defaults: Options = {
  popupId: 'powerTip',
  intentSensitivity: 7,
  intentPollInterval: 150,
  closeDelay: 150,
  placement: 'n',
  smartPlacement: true,
  defaultSize: [260, 120],
  offset: 10,
};

const smartPlacementLists: Record<PowerTip.BasePlacement, string[]> = {
  // alternating clockwise then counter-clockwise steps away from preferred placement for maximum befuddlement
  n: ['n', 'ne', 'nw', 'e', 'w', 's', 'n'],
  e: ['e', 'se', 'ne', 's', 'n', 'w', 'e'],
  s: ['s', 'sw', 'se', 'w', 'e', 'n', 's'],
  w: ['w', 'nw', 'sw', 'n', 's', 'e', 'w'],
  nw: ['nw', 'n', 'w', 'ne', 'sw', 'e', 's', 'se', 'nw'],
  ne: ['ne', 'e', 'n', 'se', 'nw', 's', 'w', 'sw', 'ne'],
  sw: ['sw', 'w', 's', 'nw', 'se', 'n', 'e', 'ne', 'sw'],
  se: ['se', 's', 'e', 'sw', 'nw', 'w', 'n', 'nw', 'se'],
};

/**
 * Public API
 */

$.powerTip = {
  show(element: WithTooltip, event?: PointerEvent) {
    if (event) {
      trackMouse(event);
      session.previousX = event.pageX;
      session.previousY = event.pageY;
      element.displayController.show();
    } else {
      element.displayController.show(true, true);
    }
    return element;
  },

  reposition(element: WithTooltip) {
    element.displayController.resetPosition();
    return element;
  },

  hide(element: WithTooltip, immediate?: boolean) {
    element.displayController.hide(immediate);
    return element;
  },

  destroy(element: Partial<WithTooltip>) {
    element.displayController?.hide(true);
  },
};

// csscoordinates.js

type Coords = {
  left: number;
  top: number;
  right: 'auto';
  bottom: 'auto';
};

function cssCoordinates(): Coords {
  return { left: 0, top: 0, right: 'auto', bottom: 'auto' };
}

// displaycontroller.js

class DisplayController {
  scoped: ScopedData = {};
  hoverTimer?: Timeout;
  el: WithTooltip;

  constructor(
    readonly element: Cash,
    readonly options: Options,
    readonly tipController: TooltipController,
  ) {
    this.el = $as(element);
    this.scoped = session.scoped[options.popupId!];
    // expose the methods
  }

  show(immediate?: boolean, forceOpen?: boolean) {
    this.cancel();
    if (!this.el.hasActiveHover) {
      if (!immediate) {
        this.scoped.tipOpenImminent = true;
        this.hoverTimer = setTimeout(() => {
          this.hoverTimer = undefined;
          this.checkForIntent();
        }, this.options.intentPollInterval);
      } else {
        if (forceOpen) {
          this.el.forcedOpen = true;
        }
        this.tipController.showTip(this.element);
      }
    }
  }

  hide(disableDelay?: boolean) {
    this.cancel();
    this.scoped.tipOpenImminent = false;
    if (this.el.hasActiveHover) {
      this.el.forcedOpen = false;
      if (!disableDelay) {
        this.scoped.delayInProgress = true;
        this.hoverTimer = setTimeout(() => {
          this.hoverTimer = undefined;
          this.tipController.hideTip(this.element);
          session.delayInProgress = false;
        }, this.options.closeDelay);
      } else {
        this.tipController.hideTip(this.element);
      }
    }
  }

  checkForIntent() {
    // calculate mouse position difference
    const xDifference = Math.abs(session.previousX - session.currentX);
    const yDifference = Math.abs(session.previousY - session.currentY);
    const totalDifference = xDifference + yDifference;

    // check if difference has passed the sensitivity threshold
    if (totalDifference < (this.options.intentSensitivity ?? 0)) {
      this.tipController.showTip(this.element);
    } else {
      // try again
      session.previousX = session.currentX;
      session.previousY = session.currentY;
      this.show();
    }
  }

  cancel() {
    clearTimeout(this.hoverTimer);
    this.scoped.delayInProgress = false;
  }

  resetPosition() {
    this.tipController.resetPosition(this.element);
  }
}

// placementcalculator.js

function placementCalculator() {
  return {
    compute(
      element: Cash,
      placement: PowerTip.Placement,
      tipWidth: number,
      tipHeight: number,
      offset: number,
    ) {
      placement = site.powertip.forcePlacementHook?.(element[0]!) ?? placement;

      const placementBase = placement.split('-')[0]; // ignore 'alt' for corners
      const coords = cssCoordinates();
      const position = getHtmlPlacement(element, placementBase);

      // calculate the appropriate x and y position in the document
      switch (placement) {
        case 'n':
          coords.left = position.left - tipWidth / 2;
          coords.top = position.top - tipHeight - offset;
          break;
        case 'e':
          coords.left = position.left + offset;
          coords.top = position.top - tipHeight / 2;
          break;
        case 's':
          coords.left = position.left - tipWidth / 2;
          coords.top = position.top + offset;
          break;
        case 'w':
          coords.left = position.left - tipWidth - offset;
          coords.top = position.top - tipHeight / 2;
          break;
        case 'nw':
          coords.left = position.left + 20 - tipWidth;
          coords.top = position.top - tipHeight - offset;
          break;
        case 'ne':
          coords.left = position.left - 20;
          coords.top = position.top - tipHeight - offset;
          break;
        case 'sw':
          coords.left = position.left + 20 - tipWidth;
          coords.top = position.top + offset;
          break;
        case 'se':
          coords.left = position.left - 20;
          coords.top = position.top + offset;
          break;
      }
      return coords;
    },
  };

  function getHtmlPlacement(element: Cash, placement: string) {
    const objectOffset = element.offset()!;
    const objectWidth = element.outerWidth();
    const objectHeight = element.outerHeight();
    let left = 0;
    let top = 0;

    // calculate the appropriate x and y position in the document
    switch (placement) {
      case 'n':
        left = objectOffset.left + objectWidth / 2;
        top = objectOffset.top;
        break;
      case 'e':
        left = objectOffset.left + objectWidth;
        top = objectOffset.top + objectHeight / 2;
        break;
      case 's':
        left = objectOffset.left + objectWidth / 2;
        top = objectOffset.top + objectHeight;
        break;
      case 'w':
        left = objectOffset.left;
        top = objectOffset.top + objectHeight / 2;
        break;
      case 'nw':
        left = objectOffset.left;
        top = objectOffset.top;
        break;
      case 'ne':
        left = objectOffset.left + objectWidth;
        top = objectOffset.top;
        break;
      case 'sw':
        left = objectOffset.left;
        top = objectOffset.top + objectHeight;
        break;
      case 'se':
        left = objectOffset.left + objectWidth;
        top = objectOffset.top + objectHeight;
        break;
    }

    return { left, top };
  }
}

// tooltipcontroller.js

class TooltipController {
  scoped: ScopedData;
  tipElement: Cash;
  placementCalculator = placementCalculator();

  constructor(readonly options: Options) {
    this.tipElement = $('#' + options.popupId);
    if (!session.scoped[options.popupId!]) session.scoped[options.popupId!] = {};
    this.scoped = session.scoped[options.popupId!];
    // build and append tooltip div if it does not already exist
    if (this.tipElement.length === 0) {
      const tip = document.createElement('div');
      tip.id = options.popupId!;
      this.tipElement = $(tip);
      $('body').append(this.tipElement);
    }

    // if we want to be able to mouse onto the tooltip then we need to attach
    // hover events to the tooltip that will cancel a close request on hover and
    // start a new close request on mouseleave
    this.tipElement.on({
      mouseenter: () => {
        if (this.scoped.activeHover) {
          this.scoped.activeHover[0].displayController.cancel();
        }
      },
      mouseleave: () => {
        if (this.scoped.activeHover) {
          this.scoped.activeHover[0].displayController.hide();
        }
      },
    });
  }

  showTip(element: Cash) {
    $as<WithTooltip>(element).hasActiveHover = true;
    this.doShowTip(element);
  }

  async doShowTip(element: Cash) {
    // it is possible, especially with keyboard navigation, to move on to
    // another element with a tooltip during the queue to get to this point
    // in the code. if that happens then we need to not proceed or we may
    // have the fadeout callback for the last tooltip execute immediately
    // after this code runs, causing bugs.
    if (!$as<WithTooltip>(element).hasActiveHover) return;

    // if the tooltip is open and we got asked to open another one then the
    // old one is still in its fadeOut cycle, so wait and try again
    if (this.scoped.isTipOpen) {
      if (!this.scoped.isClosing) {
        this.hideTip(this.scoped.activeHover);
      }
      setTimeout(() => {
        this.doShowTip(element);
      }, 100);
      return;
    }
    await this.options.render?.($as(element));

    // set tooltip position
    this.resetPosition(element);

    this.scoped.activeHover = element;
    this.scoped.isTipOpen = true;

    this.tipElement.css('visibility', 'visible');

    // start desync polling
    if (!this.scoped.desyncTimeout) {
      this.scoped.desyncTimeout = setInterval(() => this.closeDesyncedTip(), 500);
    }
  }

  hideTip(element: Cash) {
    // reset session
    this.scoped.isClosing = true;
    this.scoped.activeHover = null;
    this.scoped.isTipOpen = false;

    // stop desync polling
    this.scoped.desyncTimeout = clearInterval(this.scoped.desyncTimeout);

    // reset element state
    $as<WithTooltip>(element).hasActiveHover = false;
    $as<WithTooltip>(element).forcedOpen = false;

    // fade out
    this.tipElement.css('visibility', 'hidden');
    // move outside of viewport to hide `position: absolute` elements it contains
    const coords = cssCoordinates();
    coords.top = -9999;
    coords.left = -9999;
    this.tipElement.css(coords);

    // reset session and tooltip element
    this.scoped.isClosing = false;
    this.tipElement.removeClass();
  }

  isBasePlacement = (p: string): p is PowerTip.BasePlacement => p in smartPlacementLists;

  resetPosition(element: Cash) {
    const { placement, defaultSize } = this.options;

    if (this.options.smartPlacement && placement && this.isBasePlacement(placement)) {
      let priorityList = smartPlacementLists[placement];
      if ($as<WithTooltip>(element).classList.contains('mobile-powertip')) {
        priorityList = [...priorityList, 's']; // so that 's' is used in case all are incorrectly judged as collisions on phones
      }
      const elementWidth = this.tipElement.outerWidth() || defaultSize[0];
      const elementHeight = this.tipElement.outerHeight() || defaultSize[1];
      // iterate over the priority list and use the first placement option
      // that does not collide with the view port. If they all collide
      // then the last placement in the list will be used.
      $.each(priorityList, (_, pos: PowerTip.BasePlacement) => {
        // place tooltip and find collisions
        const coords = this.placeTooltip(element, pos);
        const collisions = getViewportCollisions(coords, elementWidth, elementHeight);
        // only attempt to nudge when the issue is horizontal overflow
        if (
          (collisions === Collision.left || collisions === Collision.right) &&
          (pos === 'n' || pos === 's')
        ) {
          const nudged = nudgeToFit(coords, collisions, elementWidth);
          if (nudged) {
            this.tipElement.css(nudged);
            return false; // stop iterating – nudge is good enough
          }
        }
        // continue/break if there were/weren't collisions (cash loop mechanism):
        return collisions !== Collision.none;
      });
    } else {
      // if placement is not a base placement (or smartPlacement is off), use regular (non-smart) logic
      this.placeTooltip(element, placement!);
    }
  }

  placeTooltip(element: Cash, placement: PowerTip.Placement) {
    let iterationCount = 0;
    let tipWidth;
    let tipHeight;
    let coords = cssCoordinates();

    // to support elastic tooltips we need to check for a change in the
    // rendered dimensions after the tooltip has been positioned
    do {
      // grab the current tip dimensions
      tipWidth = this.tipElement.outerWidth() || this.options.defaultSize[0];
      tipHeight = this.tipElement.outerHeight() || this.options.defaultSize[1];

      // get placement coordinates
      coords = this.placementCalculator.compute(
        element,
        placement,
        tipWidth,
        tipHeight,
        this.options.offset!,
      );

      // place the tooltip
      this.tipElement.css(coords);
    } while (
      // sanity check: limit to 5 iterations, and...
      ++iterationCount <= 5 &&
      // try again if the dimensions changed after placement
      (tipWidth !== this.tipElement.outerWidth() || tipHeight !== this.tipElement.outerHeight())
    );

    return coords;
  }

  closeDesyncedTip() {
    let isDesynced = false;
    // It is possible for the mouse cursor to leave an element without
    // firing the mouseleave or blur event. This most commonly happens when
    // the element is disabled under mouse cursor. If this happens it will
    // result in a desynced tooltip because the tooltip was never asked to
    // close. So we should periodically check for a desync situation and
    // close the tip if such a situation arises.
    if (this.scoped.isTipOpen && !this.scoped.isClosing && !this.scoped.delayInProgress) {
      // user moused onto another tip or active hover is disabled
      if (this.scoped.activeHover[0].hasActiveHover === false || this.scoped.activeHover.is(':disabled')) {
        isDesynced = true;
      } else {
        // hanging tip - have to test if mouse position is not over the
        // active hover and not over a tooltip set to let the user
        // interact with it.
        // for keyboard navigation: this only counts if the element does
        // not have focus.
        // for tooltips opened via the api: we need to check if it has
        // the forcedOpen flag.
        if (
          !isMouseOver(this.scoped.activeHover) &&
          !this.scoped.activeHover.is(':focus') &&
          !this.scoped.activeHover[0].forcedOpen
        ) {
          if (!isMouseOver(this.tipElement)) {
            isDesynced = true;
          }
        }
      }

      if (isDesynced) {
        // close the desynced tip
        this.hideTip(this.scoped.activeHover);
      }
    }
  }
}

// utility.js

function initTracking() {
  if (!session.mouseTrackingActive) {
    session.mouseTrackingActive = true;
    const $window = $(window);

    // grab the current viewport dimensions on load
    session.scrollLeft = window.scrollX;
    session.scrollTop = window.scrollY;
    session.windowWidth = $window.width();
    session.windowHeight = $window.height();

    // hook mouse move tracking
    document.addEventListener('mousemove', trackMouse);

    // hook viewport dimensions tracking
    window.addEventListener(
      'resize',
      function () {
        session.windowWidth = $window.width();
        session.windowHeight = $window.height();
      },
      { passive: true },
    );

    window.addEventListener(
      'scroll',
      function () {
        const x = window.scrollX;
        const y = window.scrollY;
        if (x !== session.scrollLeft) {
          session.currentX += x - session.scrollLeft;
          session.scrollLeft = x;
        }
        if (y !== session.scrollTop) {
          session.currentY += y - session.scrollTop;
          session.scrollTop = y;
        }
      },
      { passive: true },
    );
  }
}

function trackMouse(event: PointerEvent) {
  session.currentX = event.pageX;
  session.currentY = event.pageY;
}

function isMouseOver(element: Cash) {
  const elementPosition = element.offset()!;
  return (
    session.currentX >= elementPosition.left &&
    session.currentX <= elementPosition.left + element.outerWidth() &&
    session.currentY >= elementPosition.top &&
    session.currentY <= elementPosition.top + element.outerHeight()
  );
}

/**
 * Try to shift `coords` along the colliding horizontal axis so the tooltip fits
 * inside the viewport without changing its preferred placement mode.
 *
 * Strategy: only nudge when the required shift is ≤ half the tip's dimension
 * on that axis – this keeps the tooltip visually "anchored" to its preferred
 * side.
 */
function nudgeToFit(coords: Coords, collisions: number, tipWidth: number): Coords | null {
  const hasHoriz = collisions & (Collision.left | Collision.right);
  const nudged: Coords = { ...coords };
  const EDGE_OFFSET = 4;

  if (hasHoriz && typeof coords.left === 'number') {
    const vLeft = session.scrollLeft;
    const vRight = session.scrollLeft + session.windowWidth;
    if (collisions & Collision.left) {
      if (vLeft - coords.left > tipWidth / 2) return null;
      nudged.left = vLeft - EDGE_OFFSET; // shift right
    } else {
      if (coords.left + tipWidth - vRight > tipWidth / 2) return null;
      nudged.left = vRight - tipWidth - EDGE_OFFSET; // shift left
    }
  }

  return nudged;
}

function getViewportCollisions(coords: Coords, elementWidth: number, elementHeight: number) {
  const viewportTop = session.scrollTop;
  const viewportLeft = session.scrollLeft;
  const viewportBottom = viewportTop + session.windowHeight;
  const viewportRight = viewportLeft + session.windowWidth;
  let collisions = Collision.none;
  if (
    coords.top < viewportTop ||
    Math.abs(Number(coords.bottom) - session.windowHeight) - elementHeight < viewportTop
  ) {
    collisions |= Collision.top;
  }
  if (
    Number(coords.top) + elementHeight > viewportBottom ||
    Math.abs(Number(coords.bottom) - session.windowHeight) > viewportBottom
  ) {
    collisions |= Collision.bottom;
  }
  if (coords.left < viewportLeft || Number(coords.right) + elementWidth > viewportRight) {
    collisions |= Collision.left;
  }
  if (Number(coords.left) + elementWidth > viewportRight || coords.right < viewportLeft) {
    collisions |= Collision.right;
  }
  return collisions;
}
